import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import isNil from 'lodash/isNil'
import { TILE_GUARD_PROTOCOL } from '../tile-request-guard'
import { isTopoLinesLayer, TOPO_LINES_BANDS, TOPO_LINES_MIN_ZOOM } from '../../../lib/topoLineLayers'
import { activeLayerIds, visibleLayerIds } from '../../../lib/layerVisibility'
import { frameTemplateFor, liveTemplateFor, subscribeFrames } from '../../../lib/timeLayers/frameStore'
import { opacityFor, subscribeOpacity } from '../../../lib/timeLayers/opacityStore'

// Raster layers draw slightly see-through over the 3D terrain; the slider scales this.
const RASTER_OPACITY = 0.85

const normalizeUrl = url => {
  if (!url) return url
  let out
  if (__NODE_ENV__ === 'production') {
    out = typeof url === 'string' && url.includes('app.basemap.com')
      ? url.replace('app.basemap.com/layers.basemap.com', 'layers.basemap.com')
      : url
  } else {
    out = typeof url === 'string' && url.includes('staging.basemap.com')
      ? url.replace('staging.basemap.com/staging-layers.basemap.com', 'staging-layers.basemap.com')
      : url
  }
  // Route through tile guard protocol so 4xx responses are cached client-side
  // and MapLibre stops retrying them on every render frame.
  return typeof out === 'string'
    ? out.replace(/^https:\/\//, `${TILE_GUARD_PROTOCOL}://`)
    : out
}

const Render3dLayer = ({ map, activeLayers, layersDictionary }) => {
  // activeLayers id -> the concrete { sid, lid } pairs created for it. Topo lines create
  // one pair per zoom band; every other layer creates a single pair.
  const renderedRef = useRef(new Map())

  useEffect(() => {
    if (!map) return

    const sourceMaxZoom = layer => layer.upscaleZoomLevel
      ? layer.upscaleZoomLevel - 1
      : (layer.maxZoomLevel ?? 18)

    // Topo lines ship as two separately-tiled builds (zoomout for z11-16, zoomin for
    // z17+). The 2D path picks per tile request in getTileUrl, but a MapLibre raster
    // source takes a static tile template with no per-request hook — so both builds go
    // on as separate layers and the layer-level zoom range decides which is visible.
    // Without this, 3D requested the zoomin build at every zoom.
    const specsFor = (id, layer) => {
      if (!isTopoLinesLayer(id)) {
        return [{ sid: `tile-src-${id}`, lid: `tile-layer-${id}`, build: layer }]
      }
      const bands = TOPO_LINES_BANDS
        .map(band => {
          const build = layersDictionary[band.id]
          return build?.cloudfrontUrl ? { ...band, build } : null
        })
        .filter(Boolean)
      // Catalog carries only one of the two builds — let it cover the whole range rather
      // than leaving the other band blank.
      if (bands.length < TOPO_LINES_BANDS.length) {
        const only = bands[0]?.build ?? layer
        return [{
          sid: `tile-src-${only.id}`, lid: `tile-layer-${only.id}`,
          build: only, minzoom: TOPO_LINES_MIN_ZOOM,
        }]
      }
      return bands.map(band => ({
        sid: `tile-src-${band.id}`, lid: `tile-layer-${band.id}`,
        build: band.build, minzoom: band.minzoom, maxzoom: band.maxzoom,
      }))
    }

    const apply = () => {
      // Remove layers that are no longer active
      renderedRef.current.forEach((specs, id) => {
        if (!activeLayers.includes(id)) {
          specs.forEach(({ sid, lid }) => {
            try {
              if (map.getLayer(lid)) map.removeLayer(lid)
              if (map.getSource(sid)) map.removeSource(sid)
            } catch (e) {
              console.error('Render3dLayer remove error', id, e)
            }
          })
          renderedRef.current.delete(id)
        }
      })

      // Add newly activated layers
      activeLayers.forEach(id => {
        const layer = layersDictionary[id]
        if (isNil(layer) || !layer.cloudfrontUrl || renderedRef.current.has(id)) return

        const specs = specsFor(id, layer)

        try {
          specs.forEach(({ sid, lid, build, minzoom, maxzoom }) => {
            if (!map.getSource(sid)) {
              map.addSource(sid, {
                type: 'raster',
                // A time layer starts on whatever frame the time bar is showing.
                tiles: [normalizeUrl(frameTemplateFor(build.id) || liveTemplateFor(build.id, build.cloudfrontUrl))],
                tileSize: 256,
                scheme: 'tms',
                minzoom: 3,
                maxzoom: sourceMaxZoom(build),
              })
            }
            if (!map.getLayer(lid)) {
              const definition = {
                id: lid,
                type: 'raster',
                source: sid,
                paint: { 'raster-opacity': RASTER_OPACITY * opacityFor(build.id), 'raster-fade-duration': 0 },
              }
              // Only banded layers constrain visibility; everything else follows its source.
              if (!isNil(minzoom)) definition.minzoom = minzoom
              if (!isNil(maxzoom)) definition.maxzoom = maxzoom
              map.addLayer(definition)
            }
          })
          renderedRef.current.set(id, specs.map(({ sid, lid }) => ({ sid, lid })))
        } catch (e) {
          console.error('Render3dLayer add error', id, e)
        }
      })
    }

    // On every style change, clear tracking and wait for idle before re-adding layers.
    // Calling addLayer directly at style.load fires mid-terrain-initialization: tiles
    // fetch but don't render. Deferring to idle ensures the map is fully settled.
    // map.off before map.once prevents duplicate listeners if style.load fires
    // multiple times during a single style switch (e.g. setTerrain triggering a reload).
    const onStyleLoad = () => {
      renderedRef.current.clear()
      map.off('idle', apply)
      map.once('idle', apply)
    }
    map.on('style.load', onStyleLoad)

    // The time bar moved a weather layer to another frame, or the imagery picker an
    // imagery layer to another year: repoint its source. (2D swaps per tile without a gap;
    // MapLibre reloads the source, which is close enough here.)
    // The time bar's opacity slider, scaling 3D's own raster opacity.
    const unsubscribeOpacity = subscribeOpacity((id, opacity) => {
      (renderedRef.current.get(id) || []).forEach(({ lid }) => {
        try {
          if (map.getLayer(lid)) map.setPaintProperty(lid, 'raster-opacity', RASTER_OPACITY * opacity)
        } catch (e) {
          console.error('Render3dLayer opacity error', id, e)
        }
      })
    })

    const unsubscribeFrames = subscribeFrames(changed => {
      changed.forEach(id => {
        const layer = layersDictionary[id]
        const specs = renderedRef.current.get(id)
        if (!specs || !layer) return
        specs.forEach(({ sid }) => {
          try {
            map.getSource(sid)?.setTiles([normalizeUrl(frameTemplateFor(id) || liveTemplateFor(id, layer.cloudfrontUrl))])
          } catch (e) {
            console.error('Render3dLayer frame swap error', id, e)
          }
        })
      })
    })

    if (map.isStyleLoaded()) {
      apply()
    } else {
      map.once('idle', apply)
    }

    return () => {
      map.off('style.load', onStyleLoad)
      map.off('idle', apply)
      unsubscribeFrames()
      unsubscribeOpacity()
    }
  }, [map, activeLayers, layersDictionary])

  return null
}

const mapStateToProps = state => ({
  // Same eye toggle as the 2D map (WEB-7048), so switching to 3D with layers hidden keeps
  // them hidden. apply() already removes any rendered id that is no longer in this list,
  // so the filter drives both directions.
  //
  // Gated on the map tab because this component is shared with Hunt Planner's 3D map
  // (hunt-planner/map/huntPlannerMap3D.js), which reads the same active-layer setting but
  // has no eye toggle of its own — it must keep drawing every active layer.
  activeLayers: visibleLayerIds({
    activeIds: activeLayerIds(state.userSettings),
    layersDictionary: state.layersDictionary,
    layersVisible: state.selectedTab === 'map' ? state.layersVisible : true
  }),
  layersDictionary: state.layersDictionary,
})

export default connect(mapStateToProps)(Render3dLayer)
